import { Injectable, NgZone } from '@angular/core';
// import { User } from '../services/user';
import { AngularFireAuth } from '@angular/fire/compat/auth';
import { Router } from '@angular/router';
import { UserService } from '../services/user.service';
import { getToken } from 'firebase/app-check';
import { HttpClient } from '@angular/common/http';
import { PushTokenService } from './push-token.service';
import { NativeTwilioVoiceService } from './native-twilio-voice.service';
import { Subject } from 'rxjs';
import { environment } from '../../environments/environment';

@Injectable({
    providedIn: 'root',
})
export class AuthService {
    private loginSuccessSubject = new Subject<void>();
    loginSuccess$ = this.loginSuccessSubject.asObservable();

    constructor(
        public afAuth: AngularFireAuth,
        public router: Router,
        public ngZone: NgZone,
        public userService: UserService,
        public http: HttpClient,
        private pushTokenService: PushTokenService,
        private nativeTwilioVoice: NativeTwilioVoiceService
    ) {}

    /**
     * [login - Signs in to Firebase with Email + Password]
     * @param   {string} email     [Users email address]
     * @param   {string} password  [Users password]
     * @returns {Promise<any>}
     */
    async login(email: string, password: string): Promise<any> {
        const result = await this.afAuth.signInWithEmailAndPassword(email, password);
        const uid = result.user?.uid;

        if (!uid) {
            await this.cleanupFailedLogin();
            throw new Error('Unable to verify this account. Please try again.');
        }

        let response: any;
        try {
            response = await this.userService.getUserDetails(uid).toPromise();
        } catch (error) {
            await this.cleanupFailedLogin();
            throw this.createTenantLoginError(error);
        }

        if (!response?.user) {
            await this.cleanupFailedLogin();
            throw this.createTenantLoginError();
        }

        localStorage.setItem('user', JSON.stringify(response));
        this.userService.setUserAccount(response);
        this.loginSuccessSubject.next();

        this.pushTokenService.registerCurrentDevice().catch((error) => {
            console.error('Failed to register push token after login:', error);
        });

        await this.router.navigate(['conversations']);
        return response;
    }

    private async cleanupFailedLogin(): Promise<void> {
        localStorage.removeItem('user');
        await this.afAuth.signOut().catch(() => undefined);
    }

    private createTenantLoginError(error?: any): Error {
        const apiMessage = error?.error?.message || error?.message;
        const host = environment.host || window.location.host;
        const message = apiMessage && error?.status !== 401 && error?.status !== 403
            ? apiMessage
            : `This account is not available for ${host}. Please use the correct tenant URL.`;

        return new Error(message);
    }

    /**
     * [logout - Signs user out of their account]
     * @returns {Promise<any>}
     */
    logout(): Promise<any> {
        return this.nativeTwilioVoice
            .unregister()
            .catch(() => undefined)
            .then(() => this.pushTokenService.unregisterCurrentDevice())
            .catch(() => undefined)
            .then(() => this.afAuth
            .signOut()
            .then(() => {
                localStorage.removeItem('user');
                this.router.navigate(['login']);
            }));
    }

    /**
     * [isLoggedIn - Checks if user is logged in]
     * @returns {boolean}
     */
    isLoggedIn(): boolean {
        let user = JSON.parse(localStorage.getItem('user')!);
        let success = false;
    
        if(user) {
            success = true;
        }

        return success;
    }
}
